import { useState, useCallback } from 'react';

/**
 * Widget add/dismiss and widget selection (multiple matches) flow.
 */
export function useWidgetActions(deps) {
    const {
        addWidget,
        state,
        messages,
        setMessages,
        setCurrentPhase,
        setPendingWidgetId,
        pendingWidgetIdRef,
        setError,
        hasEditKeywords,
        handleSendMessageRef,
    } = deps;

    const [pendingWidget, setPendingWidget] = useState(null);
    const [widgetSelectionMatches, setWidgetSelectionMatches] = useState([]);

    const handleAddWidget = useCallback(() => {
        if (!pendingWidget) return;
        try {
            if (pendingWidget.widget && pendingWidget.layout) {
                addWidget(pendingWidget.widget, pendingWidget.layout);
                const confirmationMessage = {
                    id: `msg-${Date.now()}`,
                    role: 'assistant',
                    content: 'Element added successfully! You can now see it on your dashboard.',
                    timestamp: new Date(),
                };
                setMessages((prev) => {
                    const next = prev.map((msg) =>
                        msg.widget && msg.widget === pendingWidget
                            ? { ...msg, dismissed: false, widget: undefined }
                            : msg
                    );
                    return [...next, confirmationMessage];
                });
                setPendingWidget(null);
            } else if (pendingWidget.widgets && Array.isArray(pendingWidget.widgets)) {
                pendingWidget.widgets.forEach((item) => addWidget(item.widget, item.layout));
                const confirmationMessage = {
                    id: `msg-${Date.now()}`,
                    role: 'assistant',
                    content: `Successfully added ${pendingWidget.widgets.length} panel${pendingWidget.widgets.length > 1 ? 's' : ''} to your dashboard!`,
                    timestamp: new Date(),
                };
                setMessages((prev) => {
                    const next = prev.map((msg) =>
                        msg.widget && msg.widget === pendingWidget
                            ? { ...msg, dismissed: false, widget: undefined }
                            : msg
                    );
                    return [...next, confirmationMessage];
                });
                setPendingWidget(null);
            }
        } catch (err) {
            console.error('Error adding widget(s):', err);
            setError('Failed to add element(s). Please try again.');
        }
    }, [pendingWidget, addWidget, setMessages, setError]);

    const handleDismissWidget = useCallback(() => {
        if (!pendingWidget) return;
        setMessages((prev) =>
            prev.map((msg) => (msg.widget && msg.widget === pendingWidget ? { ...msg, dismissed: true } : msg))
        );
        setPendingWidget(null);
        setMessages((prev) => [
            ...prev,
            {
                id: `msg-${Date.now()}`,
                role: 'assistant',
                content:
                    'No problem! Would you like me to modify the panel configuration, or would you prefer to create something different?',
                timestamp: new Date(),
            },
        ]);
    }, [pendingWidget, setMessages]);

    const handleWidgetSelect = useCallback(
        async (widgetId) => {
            const widget = state.report.widgets[widgetId];
            if (!widget) return;

            const editMessages = messages.filter((m) => m.role === 'user').reverse();
            const originalEditMessage = editMessages.find((m) => hasEditKeywords(m.content)) || editMessages[0];
            if (!originalEditMessage) return;

            setPendingWidgetId(widgetId);
            if (pendingWidgetIdRef) pendingWidgetIdRef.current = widgetId;
            setWidgetSelectionMatches([]);
            setCurrentPhase('execute');

            const selectionMessageText = `Edit "${widget.title || widgetId}" - ${originalEditMessage.content}`;
            setMessages((prev) => [
                ...prev,
                {
                    id: `msg-${Date.now()}`,
                    role: 'user',
                    content: selectionMessageText,
                    timestamp: new Date(),
                },
            ]);

            const fn = handleSendMessageRef?.current;
            if (fn) {
                await fn({
                    overrideText: selectionMessageText,
                    phaseOverride: 'execute',
                    skipUserMessage: true,
                });
            }

            if (pendingWidgetIdRef) pendingWidgetIdRef.current = null;
        },
        [
            state.report.widgets,
            messages,
            hasEditKeywords,
            setPendingWidgetId,
            pendingWidgetIdRef,
            setWidgetSelectionMatches,
            setCurrentPhase,
            setMessages,
            handleSendMessageRef,
        ]
    );

    const handleWidgetSelectCancel = useCallback(() => {
        setWidgetSelectionMatches([]);
        setCurrentPhase('intent');
        setMessages((prev) => [
            ...prev,
            {
                id: `msg-${Date.now()}`,
                role: 'assistant',
                content: 'No problem! Feel free to ask me to edit an element or panel anytime.',
                timestamp: new Date(),
            },
        ]);
    }, [setCurrentPhase, setMessages, setWidgetSelectionMatches]);

    return {
        pendingWidget,
        setPendingWidget,
        widgetSelectionMatches,
        setWidgetSelectionMatches,
        handleAddWidget,
        handleDismissWidget,
        handleWidgetSelect,
        handleWidgetSelectCancel,
    };
}
